import React, { useEffect, useState, useContext } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { AppContext } from '@thd-nucleus/app-render';
import { DELETED_KEY_TAG } from '../../../constants/response-success-messages';
import { getNewPageNumber, hasNoPaymentCards } from '../../../utils/payments';
import { scrollToTop } from '../../../utils/general';
import { Pagination } from '../../shared/pagination/pagination';
import { Spinner } from '../../shared/spinner/spinner';
import { usePayments } from '../../../hooks/use-payments';
import { Alert } from '../../shared/alert/alert';
import { PaymentsHeader } from '../payments-header/payments-header';
import { CardType } from '../../../constants/payments';
import { KeytagCard } from '../keytags/keytags-card';
import { TAlertStatus } from '../../../models/common/alert-messages';
import { NoPaymentCardsMessage } from '../../shared/no-payment-cards-message/no-payment-cards-message';

export const KeytagsList: React.FC = () => {
  const [deletedCreditCardIndex, setDeletedCreditCardIndex] = useState<
    number | null
  >(null);

  const {
    loading,
    keytags,
    paginationInfo,
    currentPageNumber,
    alertMessage,
    getPaymentCards,
    setAlertMessage,
  } = usePayments();
  const { channel } = useContext(ExperienceContext);
  const { instance } = useContext(AppContext);
  const isB2B = instance?.customer?.type === 'b2b';
  const isMobile = channel === 'mobile';

  const totalPages = Number(paginationInfo?.totalPages) || 0;

  useEffect(() => {
    return () => {
      setAlertMessage(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triggerGetPaymentCards = (pageNumber: number) => {
    return getPaymentCards(CardType.KEY_TAG, false, false, {
      page: pageNumber,
    });
  };

  const getKeytagsList = () => {
    const keytagsList =
      keytags?.paymentCards?.map((account, index) => (
        <div
          key={account.cardNumber}
          className={`sui-flex ${isMobile ? 'sui-flex-col' : ''}`}
        >
          <KeytagCard
            key={account.cardNumber}
            card={account}
            setDeletedCreditCardIndex={setDeletedCreditCardIndex}
            index={index}
            isMobile={isMobile}
            onActionSuccess={handleSuccess}
            onActionFailure={handleError}
          />
        </div>
      )) || null;
    if (isMobile && deletedCreditCardIndex !== null && keytagsList !== null) {
      keytagsList.splice(deletedCreditCardIndex, 0, <>{successMessage}</>);
    }
    return keytagsList;
  };

  const handleSuccess = (cardNumber: string, message: string) => {
    setAlertMessage({
      paymentId: cardNumber,
      alertStatus: TAlertStatus.SUCCESS,
      text: message,
    });
    if (message === DELETED_KEY_TAG.message) {
      const newPage = getNewPageNumber(
        currentPageNumber,
        totalPages,
        isB2B
          ? paginationInfo?.paymentsOnCurrentPage
          : keytags?.paymentCards.length,
      );
      // ensure user stays on the same page or on the previous page (if last payment deleted) to show delete confirmation
      onChangePage(newPage);
      return;
    }

    onChangePage(1);
  };

  // We are using cardNumber because keytags do not have paymentId
  const handleError = (cardNumber: string, message: string) => {
    setAlertMessage({
      paymentId: cardNumber,
      alertStatus: TAlertStatus.ERROR,
      text: message,
    });
  };

  const onChangePage = (newPageNumber: number) => {
    triggerGetPaymentCards(newPageNumber);
  };

  const successMessage = alertMessage &&
    alertMessage.alertStatus === TAlertStatus.SUCCESS && (
      <div className="sui-mt-4 sm:sui-mt-0 lg:sui-mb-8">
        <Alert
          status={alertMessage.alertStatus}
          onClose={() => setAlertMessage(null)}
        >
          {alertMessage.text}
        </Alert>
      </div>
    );

  const desktopErrorMessage = !isMobile &&
    alertMessage &&
    alertMessage.alertStatus === TAlertStatus.ERROR && (
      <div className="lg:sui-mb-8">
        <Alert
          status={alertMessage.alertStatus}
          onClose={() => setAlertMessage(null)}
        >
          {alertMessage.text}
        </Alert>
      </div>
    );

  return (
    <>
      <Spinner active={loading} />
      <PaymentsHeader
        cardType={CardType.KEY_TAG}
        onActionFailure={handleError}
        onActionSuccess={handleSuccess}
      />
      {!isMobile && successMessage}
      {desktopErrorMessage}
      {hasNoPaymentCards(keytags) && (
        <NoPaymentCardsMessage tenderType={CardType.KEY_TAG} />
      )}
      <div className="sui-grid sui-grid-cols-1 sui-gap-y-8 sui-gap-x-8 md:sui-grid-cols-2 2xl:sui-gap-y-16 2xl:sui-grid-cols-3">
        {getKeytagsList()}
      </div>
      {totalPages > 1 && (
        <div className="sui-w-full sui-justify-center sui-p-5 sui-flex">
          <Pagination
            nPages={totalPages}
            onChangePage={(newPage) => {
              onChangePage(newPage);
              setAlertMessage(null);
              scrollToTop();
            }}
            page={currentPageNumber}
          />
        </div>
      )}
    </>
  );
};
